import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreSalesPerformanceResponse, useStoreInformation } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { ToggleChipGroup } from '../../../common/toggleChipGroup/ToggleChipGroup';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { LayoutDetails, MobileChipLayout } from '../mobileChipLayout/MobileChipLayout';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { chipDataMapping, ChipKey, ChipOption, getIndexStatus, useChipOptionsMap } from '../sales.util';
import { aggregateSalesPerformance, SalesPerformanceTableRow } from './util';

enum SalesPerformanceColumnType {
  PERIOD = 'period',
  LASTYEAR = 'ly',
  PLANNED = 'pl',
  BUDGET = 'bu',
  VSAREA = 'vsArea',
  VSSALESMARKET = 'vsSM',
  MONEYTHISYEAR = 'mnyTY',
  DIFFERENCEMONEY = 'diffMNY',
  VSLASTLASTWEEK = 'vsLLW',
  FULLPRICETAKT = 'fpTakt',
  FULLPRICETAKTVSAREA = 'fpTaktVsArea',
  REDUCEDTAKT = 'rTakt',
  REDUCEDTAKTVSAREA = 'rTaktVsArea',
}

export type SalesPerformanceWidgetProps = {
  isLoading: boolean;
  isError: boolean;
  salesPerformanceData: StoreSalesPerformanceResponse | null;
  filterIndex?: string;
  retry: () => void;
};

export const SalesPerformanceWidget: FC<SalesPerformanceWidgetProps> = observer(
  ({ isLoading, isError, salesPerformanceData, filterIndex, retry }) => {
    const { t } = useTranslation();
    const { corporateBrandId } = useStoreInformation();
    const isHMBrand = corporateBrandId === 0;

    const { chipOptionsMap } = useChipOptionsMap();
    const { salesPerformance: salesPerformanceTranslations, headerTooltips: headerTooltipsTranslations } =
      storeOverviewTranslations;
    const [selectedMobileChip, setSelectedMobileChip] = useState<ChipKey>('LCD');

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const salesData = useMemo(
      () => (salesPerformanceData ? aggregateSalesPerformance(salesPerformanceData, filterIndex) : null),
      [salesPerformanceData, filterIndex],
    );

    const headerLabels: { id: SalesPerformanceColumnType; label: string; tooltip?: string }[] = [
      {
        id: SalesPerformanceColumnType.PERIOD,
        label: t(
          storeOverviewTranslations.salesPerformance.period.key,
          storeOverviewTranslations.salesPerformance.period.defaultValue,
        ),
      },
      {
        id: SalesPerformanceColumnType.LASTYEAR,
        label: 'vs LY',
        tooltip: t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.PLANNED,
        label: 'vs PL',
        tooltip: t(headerTooltipsTranslations.vsPL.key, headerTooltipsTranslations.vsPL.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.BUDGET,
        label: 'vs BU',
        tooltip: t(headerTooltipsTranslations.vsBU.key, headerTooltipsTranslations.vsBU.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.VSAREA,
        label: 'vs Area',
        tooltip: t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue),
      },
      ...(isHMBrand
        ? [
            {
              id: SalesPerformanceColumnType.VSSALESMARKET,
              label: 'vs SM',
              tooltip: t(headerTooltipsTranslations.vsSM.key, headerTooltipsTranslations.vsSM.defaultValue),
            },
          ]
        : []),
      {
        id: SalesPerformanceColumnType.MONEYTHISYEAR,
        label: 'Mny TY',
        tooltip: t(headerTooltipsTranslations.mnyTY.key, headerTooltipsTranslations.mnyTY.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.DIFFERENCEMONEY,
        label: 'Diff Mny',
        tooltip: t(headerTooltipsTranslations.diffMny.key, headerTooltipsTranslations.diffMny.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.VSLASTLASTWEEK,
        label: 'vs LLW',
        tooltip: t(headerTooltipsTranslations.vsLLW.key, headerTooltipsTranslations.vsLLW.defaultValue),
      },
      {
        id: SalesPerformanceColumnType.FULLPRICETAKT,
        label: 'FP Takt',
        tooltip: t(headerTooltipsTranslations.fpTakt.key, headerTooltipsTranslations.fpTakt.defaultValue),
      },
      { id: SalesPerformanceColumnType.FULLPRICETAKTVSAREA, label: 'FP Takt vs Area' },
      {
        id: SalesPerformanceColumnType.REDUCEDTAKT,
        label: 'Red Takt',
        tooltip: t(
          headerTooltipsTranslations.reducedTakt.key,
          headerTooltipsTranslations.reducedTakt.defaultValue,
        ),
      },
      { id: SalesPerformanceColumnType.REDUCEDTAKTVSAREA, label: 'Red Takt vs Area' },
    ];

    const headerItems: HeaderRowItem<SalesPerformanceColumnType>[] = headerLabels.map(
      ({ id, label, tooltip }) => ({
        id,
        content: <HeaderTooltip headerText={label} tooltipText={tooltip} />,
      }),
    );

    const getTableRow = (item: SalesPerformanceTableRow) => {
      const {
        period,
        lastYear,
        planned,
        budget,
        vsArea,
        vsSalesMarket,
        moneyThisYear,
        differenceMoney,
        vsLastLastWeek,
        fullPriceTakt,
        fullPriceTaktVsArea,
        reducedTakt,
        reducedTaktVsArea,
      } = item;

      const llw = period === 'lastCompleteWeek' ? formatAmountWithSeparator(vsLastLastWeek, '%', 0) : '-';
      return [
        {
          id: SalesPerformanceColumnType.LASTYEAR,
          content: <StyledIndexTypography status={getIndexStatus(lastYear)}>{lastYear}</StyledIndexTypography>,
        },
        {
          id: SalesPerformanceColumnType.PLANNED,
          content: filterIndex ? (
            <Typography>-</Typography>
          ) : (
            <StyledIndexTypography status={getIndexStatus(planned)}>{planned}</StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.BUDGET,
          content: filterIndex ? (
            <Typography>-</Typography>
          ) : (
            <StyledIndexTypography status={getIndexStatus(budget)}>{budget}</StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.VSAREA,
          content: <StyledIndexTypography status={getIndexStatus(vsArea)}>{vsArea}</StyledIndexTypography>,
        },
        ...(isHMBrand
          ? [
              {
                id: SalesPerformanceColumnType.VSSALESMARKET,
                content: (
                  <StyledIndexTypography status={getIndexStatus(vsSalesMarket)}>
                    {vsSalesMarket}
                  </StyledIndexTypography>
                ),
              },
            ]
          : []),
        {
          id: SalesPerformanceColumnType.MONEYTHISYEAR,
          content: (
            <StyledIndexTypography status="default">
              {formatAmountWithSeparator(moneyThisYear, null, 0)}
            </StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.DIFFERENCEMONEY,
          content: (
            <StyledIndexTypography status="default">{`${differenceMoney > 0 ? '+' : ''}${formatAmountWithSeparator(
              differenceMoney,
              null,
              0,
            )}`}</StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.VSLASTLASTWEEK,
          content: <StyledIndexTypography status="default">{llw}</StyledIndexTypography>,
        },
        {
          id: SalesPerformanceColumnType.FULLPRICETAKT,
          content: (
            <StyledIndexTypography status={getIndexStatus(fullPriceTakt)}>{fullPriceTakt}</StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.FULLPRICETAKTVSAREA,
          content: (
            <StyledIndexTypography status={getIndexStatus(fullPriceTaktVsArea)}>
              {fullPriceTaktVsArea}
            </StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.REDUCEDTAKT,
          content: (
            <StyledIndexTypography status={getIndexStatus(reducedTakt)}>{reducedTakt}</StyledIndexTypography>
          ),
        },
        {
          id: SalesPerformanceColumnType.REDUCEDTAKTVSAREA,
          content: (
            <StyledIndexTypography status={getIndexStatus(reducedTaktVsArea)}>
              {reducedTaktVsArea}
            </StyledIndexTypography>
          ),
        },
      ];
    };

    const transformedData = useMemo(
      () =>
        salesData
          ? [
              { metricName: 'Selected day', ...salesData.currentDay },
              { metricName: 'Curr Week', ...salesData.currentWeek },
              { metricName: 'LCW', ...salesData.lastCompleteWeek },
              { metricName: 'L4Wks', ...salesData.lastFourWeeks },
              { metricName: 'Acc TY', ...salesData.accumulatedFinancialYearToDate },
            ]
          : [],
      [salesData],
    );

    const getSalesDetailsForMobile = (item: SalesPerformanceTableRow): LayoutDetails[] => {
      const { lastYear, planned, budget, vsArea, vsSalesMarket } = item;
      const salesMarketItem: LayoutDetails[] = [
        { label: 'vs SM', value: vsSalesMarket.toString(), status: getIndexStatus(vsSalesMarket) },
      ];
      return [
        { label: 'vs LY', value: lastYear.toString(), status: getIndexStatus(lastYear) },
        { label: 'vs PL', value: planned.toString(), status: getIndexStatus(planned) },
        { label: 'vs BU', value: budget.toString(), status: getIndexStatus(budget) },
        { label: 'vs Area', value: vsArea.toString(), status: getIndexStatus(vsArea) },
        ...(isHMBrand ? salesMarketItem : []),
      ];
    };

    const getMoneyDetailsForMobile = (item: SalesPerformanceTableRow): LayoutDetails[] => {
      const {
        period,
        moneyThisYear,
        differenceMoney,
        vsLastLastWeek,
        fullPriceTakt,
        reducedTakt,
        fullPriceTaktVsArea,
        reducedTaktVsArea,
      } = item;

      const llw = period === 'lastCompleteWeek' ? formatAmountWithSeparator(vsLastLastWeek, '%', 0) : '-';

      return item
        ? [
            {
              label: 'Mny TY',
              value: formatAmountWithSeparator(moneyThisYear, null, 0),
              status: 'default',
            },
            {
              label: 'Diff Mny',
              value: `${differenceMoney > 0 ? '+' : ''}${formatAmountWithSeparator(differenceMoney, null, 0)}`,
              status: 'default',
            },
            {
              label: 'vs LLW',
              value: llw,
              status: 'default',
            },
            {
              label: 'FP Takt',
              value: fullPriceTakt.toString(),
              status: getIndexStatus(fullPriceTakt),
            },
            {
              label: 'FP Takt vs Area',
              value: fullPriceTaktVsArea.toString(),
              status: getIndexStatus(fullPriceTaktVsArea),
            },
            {
              label: 'Red Takt',
              value: reducedTakt.toString(),
              status: getIndexStatus(reducedTakt),
            },
            {
              label: 'Red Takt vs Area',
              value: reducedTaktVsArea.toString(),
              status: getIndexStatus(reducedTaktVsArea),
            },
          ]
        : [];
    };

    const mobileChipOptions: ChipOption[] = [
      chipOptionsMap.LCD,
      chipOptionsMap.CW,
      chipOptionsMap.LCW,
      chipOptionsMap.L4Wks,
      chipOptionsMap.AccTY,
    ];

    return (
      <StoreWidgetContent
        widgetVariant="default"
        title={t(salesPerformanceTranslations.title.key, salesPerformanceTranslations.title.defaultValue)}
        tooltipText={t(
          salesPerformanceTranslations.tooltip.key,
          salesPerformanceTranslations.tooltip.defaultValue,
        )}
        errorRetryHandler={retry}
        isLoading={isLoading}
        isError={isError}
        isNoData={!salesData}
        errorText={t(
          salesPerformanceTranslations.errorText.key,
          salesPerformanceTranslations.errorText.defaultValue,
        )}
        widgetContent={
          <ColumnFlexContainer>
            {!isMobile && salesData && (
              <ColumnFlexContainer
                padding={[2, 0, 2]}
                flex={1}
                minHeight={[0]}
                maxHeight={[80]}
                data-testid="sales-performance-table-wrapper"
              >
                <Table
                  items={transformedData}
                  headerItems={headerItems}
                  stickyHeader
                  stickyFirstColumn
                  sxProps={{ padding: '14px 8px' }}
                  headerSxProps={{ padding: '8px' }}
                  firstCellMinWidth="100px"
                  getRowItems={(item) => {
                    const row = getTableRow(item);
                    return [
                      {
                        id: SalesPerformanceColumnType.PERIOD,
                        content: <Typography type="body3">{item.metricName}</Typography>,
                      },
                      ...row,
                    ];
                  }}
                />
              </ColumnFlexContainer>
            )}

            {isMobile && salesData && (
              <>
                <ToggleChipGroup
                  chipOptions={mobileChipOptions}
                  selectedChip={selectedMobileChip}
                  setSelectedChip={(chip: ChipKey) => {
                    setSelectedMobileChip(chip);
                  }}
                />
                <ColumnFlexContainer overflow="auto" maxHeight="260px" gap={[3]}>
                  <MobileChipLayout
                    layout="grid"
                    layoutTitle={t(
                      salesPerformanceTranslations.salesText.key,
                      salesPerformanceTranslations.salesText.defaultValue,
                    )}
                    layoutData={getSalesDetailsForMobile(salesData[chipDataMapping[selectedMobileChip]])}
                  />
                  <MobileChipLayout
                    layout="grid"
                    layoutTitle={t(
                      salesPerformanceTranslations.moneyText.key,
                      salesPerformanceTranslations.moneyText.defaultValue,
                    )}
                    layoutData={getMoneyDetailsForMobile(salesData[chipDataMapping[selectedMobileChip]])}
                  />
                </ColumnFlexContainer>
              </>
            )}
          </ColumnFlexContainer>
        }
      />
    );
  },
);
